import { DEFAULT_OBJECT_SIZE, MAP_SIZE } from './constants'
import type { ObjectInfo, MapObject, Map } from '@mission.io/mission-toolkit'

/**
 * Takes a pair of coordinates for a map and scales them for viewing
 * @param {Array} [x,y] The tuple of values to scale
 * @param {number} size The size of the drawing area (if circle, diameter)
 * @return {Array} returns a scaled [x,y] coordinate pair
 */
function toViewCoordinates(
	[x, y]: [number, number],
	mapSize: number
): [number, number] {
	const originalMapSize = 300
	const newX = (x * mapSize) / originalMapSize
	const newY = (y * mapSize) / originalMapSize
	return [newX + mapSize / 2, newY + mapSize / 2]
}

/**
 * Given an svg object, the x, y coordinates will be at its top left corner,
 * this changes the coordinates so that the center of the object is the coordinate base.
 * @param  {[number, number]} [x, y]   coordinates
 * @param  {[number, number]} sizes [width, height] of the object being placed
 * @return {[number, number]}      new centered coordinates
 */
function centerCoordinates(
	[x, y]: [number, number],
	size: [number, number]
): [number, number] {
	return [x - size[0] / 2, y - size[1] / 2]
}

/**
 * Scales the object size relative to the mapSize
 */
function scaledObjectSize(
	mapSize: number,
	objectType: string | { width: number, height: number }
): [number, number] {
	const originMapSize = 300
	let sizes = []
	if (typeof objectType === 'string') {
		const size = (mapSize / originMapSize) * DEFAULT_OBJECT_SIZE
		sizes = [size, size]
	} else {
		sizes = [objectType.width, objectType.height]
	}

	return sizes
}

/**
 * Returns important data for knowing the placement and sizing of an object element within the svg map element
 * centeredX and centeredY represent where the object will be located on the map
 * viewX and viewY represent where the objects option selector will be located on the map
 * 		If an object intersects with the edge of the map, then viewX and viewY will change in order to remain on the map
 * size is the size of the object.
 * @param {'OTHER'} objectType
 * @param {MapObject} mapObject
 * @param {?{width: number, height: number}} sizeData
 * @param {boolean} shouldCenterCoordinates
 */
export function getMapObjectData(
	objectType: 'OTHER',
	mapObject: MapObject,
	sizeData: ?{ width: number, height: number },
	shouldCenterCoordinates?: boolean = true
): {
	sizes: [number, number],
	viewX: number,
	viewY: number,
	centeredX: number,
	centeredY: number,
} {
	let sizes: [number, number] = scaledObjectSize(
		MAP_SIZE,
		sizeData || objectType
	)
	let [viewX, viewY] = toViewCoordinates(
		[mapObject.location.x, -mapObject.location.y],
		MAP_SIZE
	)
	const [centeredX, centeredY] = shouldCenterCoordinates
		? centerCoordinates([viewX, viewY], sizes)
		: [viewX, viewY]

	return { sizes, viewX, viewY, centeredX, centeredY }
}

// Checks to see if an object has been removed from the map and is visible.
export function isObjectVisible(objectInfo: ?ObjectInfo): boolean {
	if (!objectInfo) {
		return false
	}
	if (objectInfo.isHidden) {
		return false
	}
	return true
}

const mapObjectKeys = ['other']
export function forEachMapObject(
	map: Map,
	cb: (string, MapObject, number) => void
) {
	mapObjectKeys.forEach((key: string) => {
		map.objects[key].forEach((body: MapObject, index: number) => {
			cb(key, body, index)
		})
	})
}
